import isFunction from 'lodash/isFunction'
import isEmpty from 'lodash/isEmpty'
import axios from 'axios'

import { GATEWAY_DOMAIN } from '@/base/constants/ConfigVars'
import { getCookieAuth, getCurrentAccessToken, getCurrentRefreshToken, isAuthError, logout, setRefreshedAcccessToken } from './utils/session'
import { defaultParamsSerializer } from './utils/params-serializer'
import { mockAdapter } from './utils/mock-adapter'
import { ApiError, processError } from './utils/api-error'

let isRedirecting = false
let isRefreshing = false
let failedQueue = []

function redirectToLogin(errors = []) {
  if (isRedirecting)
    return
  isRedirecting = true
  logout(errors)
}

function processQueue(error, token = null) {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error)
    }
    else {
      promise.resolve(token)
    }
  })
  failedQueue = []
}

const defaultConfig = {
  baseURL: GATEWAY_DOMAIN,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: defaultParamsSerializer,
}

const apiClient = axios.create(defaultConfig)

// Mock interceptor
apiClient.interceptors.request.use((config) => {
  if (config.mock) {
    config.adapter = mockAdapter
    config.disableAuthCheck = true
    config.baseURL = '' // Disable baseURL for mock requests
  }
  return config
})

apiClient.interceptors.request.use(
  (config) => {
    if (!config?.disableAuthCheck) {
      const userAuth = getCookieAuth()
      if (!userAuth) {
        const error = new ApiError('User not authenticated')
        error.config = config
        return Promise.reject(error)
      }

      const token = getCurrentAccessToken()
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }

    return config
  },
  error => Promise.reject(processError(error)),
)

apiClient.interceptors.response.use(
  (response) => {
    if (!response?.config?.disableAuthCheck) {
      const newJwt = response.headers?.['ma-jwt']
      if (newJwt) {
        isRefreshing = false
        setRefreshedAcccessToken(newJwt)
        processQueue(null, newJwt)
      }
    }

    const res = response.data

    if (!res.success) {
      const message = res.errorMessage || 'API Error'
      const errors = Array.isArray(res.errors)
        ? res.errors.map(err => ({ message: err }))
        : []
      const error = new ApiError(message, {
        statusCode: response.status,
        errorCode: res?.errorCode,
        errors,
      })
      return Promise.reject(error)
    }

    if (isEmpty(res?.data)) {
      res.data = null
    }

    if (isFunction(response?.config?.transformData)) {
      return response.config.transformData(res.data)
    }

    return res.data
  },
  async (error) => {
    try {
      if (error?.config?.disableAuthCheck) {
        return Promise.reject(processError(error))
      }

      const originalRequest = error.config
      const errors = error.response?.data?.errors ?? []

      if (error.response?.status === 401 || isAuthError(errors)) {
        if (originalRequest._retry) {
          redirectToLogin(errors)
          processQueue(error, null)
          return Promise.reject(processError(error))
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`
                resolve(apiClient(originalRequest))
              },
              reject,
            })
          })
        }

        originalRequest._retry = true
        isRefreshing = true

        const refreshToken = getCurrentRefreshToken()
        if (!refreshToken) {
          redirectToLogin(errors)
          processQueue(new Error('No refresh token available'), null)
          return Promise.reject(processError(error))
        }

        try {
          originalRequest.headers.Authorization = `Bearer ${refreshToken}`
          const response = await apiClient(originalRequest)
          const newJwt = response.headers?.['ma-jwt']

          if (newJwt) {
            setRefreshedAcccessToken(newJwt)
          }

          const token = getCurrentAccessToken()
          processQueue(null, newJwt || token)
          return response
        }
        catch (refreshError) {
          processQueue(refreshError, null)
          return Promise.reject(processError(refreshError))
        }
        finally {
          isRefreshing = false
        }
      }

      return Promise.reject(processError(error))
    }
    catch (err) {
      return Promise.reject(processError(err))
    }
    finally {
      isRedirecting = false
    }
  },
)

export { apiClient }
